/**
 * WebRTC Service
 * 
 * Gerencia conexões WebRTC com o Janus Gateway para streaming de câmeras ao vivo.
 * 
 * Fluxo de conexão:
 * 1. createSession() - Cria sessão no backend/Janus
 * 2. Recebe SDP offer do servidor
 * 3. Cria RTCPeerConnection local
 * 4. setRemoteDescription() com o offer
 * 5. createAnswer() e setLocalDescription()
 * 6. Envia answer para o servidor
 * 7. Troca ICE candidates
 * 8. Stream começa a fluir!
 */

import { API_BASE_URL, authFetch } from './authFetch';

export interface WebRTCSessionConfig {
  cameraId: number;
  onTrack?: (event: RTCTrackEvent) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onIceConnectionStateChange?: (state: RTCIceConnectionState) => void;
  onError?: (error: Error) => void;
}

export interface WebRTCSession {
  sessionId: number;
  handleId: number;
  streamId: number;
  cameraId: number;
  cameraName: string;
  peerConnection: RTCPeerConnection;
  remoteStream: MediaStream | null;
}

export interface ICEServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

// Estado global das sessões ativas
const activeSessions: Map<number, WebRTCSession> = new Map();

/**
 * Obtém servidores ICE do backend
 */
export async function getIceServers(): Promise<ICEServer[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/streaming/ice-servers`);
    if (response.ok) {
      const data = await response.json();
      return data.iceServers || [];
    }
  } catch {
    // Usar servidores ICE padrão
  }
  
  // Fallback para servidores públicos
  return [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ];
}

/**
 * Cria uma nova conexão WebRTC para visualizar uma câmera
 */
export async function createWebRTCSession(config: WebRTCSessionConfig): Promise<WebRTCSession> {
  const { cameraId, onTrack, onConnectionStateChange, onIceConnectionStateChange, onError } = config;
  
  try {
    // 1. Criar sessão no backend (que cria no Janus)
    const response = await authFetch(`${API_BASE_URL}/streaming/webrtc/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ camera_id: cameraId })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Falha ao criar sessão WebRTC');
    }
    
    const sessionData = await response.json();
    
    if (!sessionData.success || !sessionData.jsep) {
      throw new Error('Servidor não retornou SDP offer válido');
    }
    
    const { session_id, handle_id, stream_id, camera_name, jsep, ice_servers } = sessionData;
    
    // 2. Obter ICE servers (do backend ou fallback)
    const iceServers = ice_servers || await getIceServers();
    
    // 3. Criar RTCPeerConnection
    const pc = new RTCPeerConnection({
      iceServers: iceServers,
      iceCandidatePoolSize: 10
    });
    
    // 4. Configurar handlers de evento
    const remoteStream = new MediaStream();
    
    pc.ontrack = (event) => {
      remoteStream.addTrack(event.track);
      onTrack?.(event);
    };
    
    pc.onconnectionstatechange = () => {
      onConnectionStateChange?.(pc.connectionState);
      
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        onError?.(new Error(`Conexão ${pc.connectionState}`));
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      onIceConnectionStateChange?.(pc.iceConnectionState);
    };
    
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        // Enviar candidate para o servidor (Trickle ICE)
        try {
          await authFetch(`${API_BASE_URL}/streaming/webrtc/ice-candidate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id,
              handle_id,
              candidate: event.candidate.toJSON()
            })
          });
        } catch {
          // Erro ao enviar ICE candidate
        }
      } else {
        // ICE gathering completo
        try {
          await authFetch(`${API_BASE_URL}/streaming/webrtc/ice-complete?session_id=${session_id}&handle_id=${handle_id}`, {
            method: 'POST'
          });
        } catch {
          // Erro ao sinalizar ICE complete
        }
      }
    };
    
    // 5. Processar SDP offer do servidor
    await pc.setRemoteDescription(new RTCSessionDescription({
      type: 'offer',
      sdp: jsep.sdp
    }));
    
    // 6. Criar e enviar answer
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    
    // 7. Enviar answer para o servidor
    const answerResponse = await authFetch(`${API_BASE_URL}/streaming/webrtc/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id,
        handle_id,
        sdp_answer: answer.sdp
      })
    });
    
    if (!answerResponse.ok) {
      throw new Error('Falha ao enviar SDP answer');
    }
    
    // 8. Criar objeto de sessão
    const session: WebRTCSession = {
      sessionId: session_id,
      handleId: handle_id,
      streamId: stream_id,
      cameraId,
      cameraName: camera_name,
      peerConnection: pc,
      remoteStream
    };
    
    // Armazenar sessão ativa
    activeSessions.set(cameraId, session);

    return session;
    
  } catch (error) {
    onError?.(error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Fecha uma sessão WebRTC
 */
export async function closeWebRTCSession(cameraId: number): Promise<void> {
  const session = activeSessions.get(cameraId);
  
  if (!session) return;
  
  try {
    // Fechar PeerConnection
    session.peerConnection.close();
    
    // Notificar servidor
    await authFetch(`${API_BASE_URL}/streaming/webrtc/session/${session.sessionId}`, {
      method: 'DELETE'
    });
    
    // Remover do cache
    activeSessions.delete(cameraId);

  } catch {
    // Ainda assim, limpar localmente
    activeSessions.delete(cameraId);
  }
}

/**
 * Controla o stream (pause, resume, switch)
 */
export async function controlStream(
  cameraId: number, 
  action: 'start' | 'stop' | 'pause' | 'switch',
  newStreamId?: number
): Promise<boolean> {
  const session = activeSessions.get(cameraId);
  
  if (!session) return false;
  
  try {
    const response = await authFetch(`${API_BASE_URL}/streaming/webrtc/control`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: session.sessionId,
        handle_id: session.handleId,
        action,
        stream_id: newStreamId
      })
    });
    
    return response.ok;
    
  } catch {
    return false;
  }
}

/**
 * Obtém sessão ativa para uma câmera
 */
export function getActiveSession(cameraId: number): WebRTCSession | undefined {
  return activeSessions.get(cameraId);
}

/**
 * Lista todas as sessões ativas
 */
export function getActiveSessions(): WebRTCSession[] {
  return Array.from(activeSessions.values());
}

/**
 * Fecha todas as sessões ativas
 */
export async function closeAllSessions(): Promise<void> {
  const cameraIds = Array.from(activeSessions.keys());
  
  await Promise.all(
    cameraIds.map(id => closeWebRTCSession(id))
  );
}

/**
 * Lista streams disponíveis no servidor
 */
export async function listAvailableStreams(): Promise<any[]> {
  try {
    const response = await authFetch(`${API_BASE_URL}/streaming/webrtc/streams`);
    
    if (response.ok) {
      const data = await response.json();
      return data.streams || [];
    }
    
    return [];
  } catch {
    return [];
  }
}

/**
 * Verifica saúde dos serviços de streaming
 */
export async function checkStreamingHealth(): Promise<{
  janus: { status: string; url: string };
  shinobi: { status: string; url: string };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/streaming/health`);
    
    if (response.ok) {
      return await response.json();
    }
    
    throw new Error('Health check failed');
  } catch (error) {
    return {
      janus: { status: 'offline', url: '' },
      shinobi: { status: 'offline', url: '' }
    };
  }
}

/**
 * Adapter para usar stream em elemento <video>
 */
export function attachStreamToVideo(
  session: WebRTCSession, 
  videoElement: HTMLVideoElement
): void {
  if (session.remoteStream) {
    videoElement.srcObject = session.remoteStream;
    videoElement.play().catch(() => {});
  }
}

/**
 * Hook para React - gerencia ciclo de vida da conexão
 */
export function useWebRTCConnection(cameraId: number | null) {
  // Este hook seria usado em componentes React
  // Por enquanto, apenas retorna as funções utilitárias
  return {
    createSession: (config: Omit<WebRTCSessionConfig, 'cameraId'>) => 
      cameraId ? createWebRTCSession({ ...config, cameraId }) : Promise.reject('No camera'),
    closeSession: () => cameraId ? closeWebRTCSession(cameraId) : Promise.resolve(),
    getSession: () => cameraId ? getActiveSession(cameraId) : undefined
  };
}

